import * as types from '../types/articleTypes';
import * as articleServices from '../../services/articleServices';

// Fetch all articles
export const fetchArticles = (params = {}) => async (dispatch) => {
  dispatch({ type: types.FETCH_ARTICLES_REQUEST });
  try {
    const response = await articleServices.getAllArticles(params);
    dispatch({ 
      type: types.FETCH_ARTICLES_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching articles';
    dispatch({ type: types.FETCH_ARTICLES_FAILURE, payload: errorMessage });
  }
};

// Fetch article by slug (with optional language and region).
// Retries transient network/5xx errors so Googlebot does not soft-404 → noindex.
export const fetchArticleBySlug = (slug, lang = null, region = null) => async (dispatch) => {
  dispatch({ type: types.FETCH_ARTICLE_BY_SLUG_REQUEST, payload: { slug } });

  const maxAttempts = 3;
  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await articleServices.getArticleBySlug(slug, lang, region);
      dispatch({
        type: types.FETCH_ARTICLE_BY_SLUG_SUCCESS,
        payload: response.data || response,
        meta: { slug },
      });
      return;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      const isNotFound = status === 404;
      const isForbidden = status === 403;
      const isTransient = !status || status >= 500 || status === 408 || status === 429;

      if (isNotFound || isForbidden || !isTransient || attempt === maxAttempts) {
        const errorMessage =
          error.response?.data?.message || error.message || 'An error occurred while fetching the article';
        dispatch({
          type: types.FETCH_ARTICLE_BY_SLUG_FAILURE,
          payload: errorMessage,
          meta: { slug, status: status || null, notFound: Boolean(isNotFound || isForbidden) },
        });
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
    }
  }

  const errorMessage =
    lastError?.response?.data?.message || lastError?.message || 'An error occurred while fetching the article';
  dispatch({
    type: types.FETCH_ARTICLE_BY_SLUG_FAILURE,
    payload: errorMessage,
    meta: { slug, status: lastError?.response?.status || null, notFound: false },
  });
};

// Fetch top articles (with optional language/region)
export const fetchTopArticles = (limit = 5, params = {}) => async (dispatch) => {
  dispatch({ type: types.FETCH_TOP_ARTICLES_REQUEST });
  try {
    const response = await articleServices.getTopArticles(limit, params);
    dispatch({ 
      type: types.FETCH_TOP_ARTICLES_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching top articles';
    dispatch({ type: types.FETCH_TOP_ARTICLES_FAILURE, payload: errorMessage });
  }
};

// Fetch popular articles (with optional language/region)
export const fetchPopularArticles = (limit = 10, params = {}) => async (dispatch) => {
  dispatch({ type: types.FETCH_POPULAR_ARTICLES_REQUEST });
  try {
    const response = await articleServices.getPopularArticles(limit, params);
    dispatch({ 
      type: types.FETCH_POPULAR_ARTICLES_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching popular articles';
    dispatch({ type: types.FETCH_POPULAR_ARTICLES_FAILURE, payload: errorMessage });
  }
};

// Fetch trending articles (with optional language/region)
export const fetchTrendingArticles = (limit = 10, params = {}) => async (dispatch) => {
  dispatch({ type: types.FETCH_TRENDING_ARTICLES_REQUEST });
  try {
    const response = await articleServices.getTrendingArticles(limit, params);
    dispatch({ 
      type: types.FETCH_TRENDING_ARTICLES_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching trending articles';
    dispatch({ type: types.FETCH_TRENDING_ARTICLES_FAILURE, payload: errorMessage });
  }
};

// Fetch similar articles for an article detail sidebar
export const fetchSimilarArticles = (params = {}) => async (dispatch) => {
  dispatch({ type: types.FETCH_SIMILAR_ARTICLES_REQUEST });
  try {
    const response = await articleServices.getSimilarArticles(params);
    dispatch({
      type: types.FETCH_SIMILAR_ARTICLES_SUCCESS,
      payload: response.data || response,
    });
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || 'An error occurred while fetching similar articles';
    dispatch({ type: types.FETCH_SIMILAR_ARTICLES_FAILURE, payload: errorMessage });
  }
};

// Fetch featured article
export const fetchFeaturedArticle = (limit = 1) => async (dispatch) => {
  dispatch({ type: types.FETCH_FEATURED_ARTICLE_REQUEST });
  try {
    const response = await articleServices.getFeaturedArticle(limit);
    dispatch({ 
      type: types.FETCH_FEATURED_ARTICLE_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching featured article';
    dispatch({ type: types.FETCH_FEATURED_ARTICLE_FAILURE, payload: errorMessage });
  }
};

// Create article (Admin only)
export const createArticle = (articleData) => async (dispatch) => {
  dispatch({ type: types.CREATE_ARTICLE_REQUEST });
  try {
    const response = await articleServices.createArticle(articleData);
    dispatch({ 
      type: types.CREATE_ARTICLE_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while creating the article';
    dispatch({ type: types.CREATE_ARTICLE_FAILURE, payload: errorMessage });
  }
};

// Update article (Admin only)
export const updateArticle = (id, articleData) => async (dispatch) => {
  dispatch({ type: types.UPDATE_ARTICLE_REQUEST });
  try {
    const response = await articleServices.updateArticle(id, articleData);
    dispatch({ 
      type: types.UPDATE_ARTICLE_SUCCESS, 
      payload: response.data || response 
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while updating the article';
    dispatch({ type: types.UPDATE_ARTICLE_FAILURE, payload: errorMessage });
  }
};

// Delete article (Admin only)
export const deleteArticle = (id) => async (dispatch) => {
  dispatch({ type: types.DELETE_ARTICLE_REQUEST });
  try {
    await articleServices.deleteArticle(id);
    dispatch({ type: types.DELETE_ARTICLE_SUCCESS, payload: id });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while deleting the article';
    dispatch({ type: types.DELETE_ARTICLE_FAILURE, payload: errorMessage });
  }
};


